import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import ky, { HTTPError } from 'ky';

import { getLoginCheckData } from './workBackendSlice';

import { Hash, setTokensToCookie } from '../general';
import { apiURL } from '../components/DigimagiProvider';

export type GoodsKindType = 'book' | 'card_template' | 'palette'
export type GoodsStatusType = 'public' | 'private' | 'closed'
export const isGoodsStatusType = (value: unknown): value is GoodsStatusType => {
  return value === 'public' || value === 'private' || value === 'closed';
}

export type GoodsDb = {
  owner_id: string;
  price: string;
  name: string;
  kind: GoodsKindType;
  permission_overwrite: boolean;
  permission_new_save: boolean;
  permission_used_card_redesign: boolean;
  force_update: boolean;
  description: string;
  status: GoodsStatusType;
  latest_version: number;
  registration_date: string;
  last_updateds_at?: string;
  deleted_at?: string;
}
export type GoodsDbHashType = Hash<GoodsDb>

export type PurchaseDb = {
  goods_id: string;
  version: number;
  registration_date: string;
}
export type PurchaseDbHashType = Hash<PurchaseDb>

export type ShopState = {
  open: boolean;
  notice: number;
  goods: GoodsDbHashType;
  ownGoods: GoodsDbHashType;
  purchases: PurchaseDbHashType;
};

const initialState: ShopState = {
  open: false,
  notice: 0,
  goods: {},
  ownGoods: {},
  purchases: {}
}

export type PurchaseLoadPayload = {
  id: string;
  user_id?: string;
} & PurchaseDb;

const shopSlice = createSlice({
  name: 'shop',
  initialState,
  reducers: {
    openShop(state) {
      state.open = true;
    },
    closeShop(state) {
      state.open = false;
    },
    loadPurchase(state: ShopState, action: PayloadAction<PurchaseLoadPayload>) {
      const { id, user_id, ...value } = action.payload;
      if (state.purchases[id]) {
        console.log(`purchase ${id} exists`);
        return;
      }
      state.purchases[id] = value;
    },
    clear(_state, _action: PayloadAction<void>) {
      return {...initialState}
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getPublicGoods.fulfilled, (state: ShopState, action) => {
      if (action.payload) state.goods = action.payload;
    }),
    builder.addCase(getOwnGoods.fulfilled, (state: ShopState, action) => {
      if (action.payload) state.ownGoods = action.payload;
      else state.ownGoods = {};
    }),
    builder.addCase(getPurchases.fulfilled, (state: ShopState, action) => {
      if (action.payload) state.purchases = action.payload;
      else state.purchases = {};
    })
  }
});

export const getPublicGoods = createAsyncThunk(
  'shop/getPublicGoodsStatus',
  async () => {
    try {
      const query = await ky.get(`${apiURL}get_public_goods`).json() as any;
      const data = query.data as { id: string; }[];
      const hash: GoodsDbHashType = {};
      data.forEach(record => {
        const { id, ...obj } = record;
        if ('last_updated_at' in obj && obj.last_updated_at === null) obj.last_updated_at = undefined;
        if ('deleted_at' in obj) {
          if (obj.deleted_at === null) delete obj.deleted_at;
          else return;
        }
        hash[id] = obj as GoodsDb;
      })
      return hash;
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 400) console.log(err.response);
      else console.log(err);
    }
  }
)
export const getOwnGoods = createAsyncThunk(
  'shop/getOwnGoodsStatus',
  async () => {
    try {
      const sendData = getLoginCheckData();
      const query = await ky.post(`${apiURL}get_own_goods`, { json: sendData }).json() as any;
      const data = query.data;
      const newToken = data.newToken;
      if (newToken && typeof newToken === 'object') setTokensToCookie(newToken);

      const goodsData = data.goods as { id: string; }[];
      const hash: GoodsDbHashType = {};
      goodsData.forEach(record => {
        const { id, ...obj } = record;
        if ('last_updated_at' in obj && obj.last_updated_at === null) obj.last_updated_at = undefined;
        hash[id] = obj as GoodsDb;
      })
      return hash;
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 400) console.log(err.response);
      else console.log(err);
    }
  }
)
export const getPurchases = createAsyncThunk(
  'shop/getPurchasesStatus',
  async () => {
    try {
      const sendData = getLoginCheckData();
      const query = await ky.post(`${apiURL}get_purchases`, { json: sendData }).json() as any;
      const data = query.data;
      const newToken = data.newToken;
      if (newToken && typeof newToken === 'object') setTokensToCookie(newToken);

      const purchasesData = data.purchases as { id: string; user_id: string; }[];
      const hash: PurchaseDbHashType = {};
      purchasesData.forEach(record => {
        const { id, user_id, ...obj } = record;
        hash[id] = obj as PurchaseDb;
      })
      return hash;
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 400) console.log(err.response);
      else console.log(err);
    }
  }
)

export const { openShop, closeShop, loadPurchase, clear } = shopSlice.actions;
export default shopSlice.reducer;
